import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { ArrowLeft } from 'lucide-react'
import Footer from '../components/Footer'

interface LoginForm {
  username: string
  password: string
}

export default function StaffLoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const { staffSignIn, staff } = useAuth()
  const navigate = useNavigate()
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginForm>()

  // Handle navigation when staff is logged in
  useEffect(() => {
    if (staff) {
      navigate(staff.role === 'admin' ? '/admin' : '/staff/dashboard')
    }
  }, [staff, navigate])

  // Load remembered username on component mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('rememberedStaffUsername')
    if (rememberedUsername) {
      setValue('username', rememberedUsername)
      setRememberMe(true)
    }
  }, [setValue])

  // Don't render the form if already logged in
  if (staff) {
    return null
  }

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      await staffSignIn(data.username, data.password)
      
      // Save username for remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberedStaffUsername', data.username)
      } else {
        localStorage.removeItem('rememberedStaffUsername')
      }
      
      toast.success('Welcome back!')
      // Navigation will be handled by useEffect when staff state updates
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <header className="bg-dark-100 shadow-sm border-b border-dark-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <Link to="/" className="p-2 text-vapour-600 hover:text-vapour-900 mr-2">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-xl font-bold text-vapour-900">Staff Login</h1>
        </div>
      </header>

      <main className="flex items-center justify-center p-4 mt-8">
        <div className="w-full max-w-md">
          <div className="bg-dark-100 rounded-lg shadow-xl p-6 border border-dark-200">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-vapour-900 mb-2">Staff Portal</h2>
              <p className="text-vapour-600">Sign in to scan QR codes and manage rewards</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-vapour-700 mb-1">
                  Username
                </label>
                <input
                  {...register('username', { required: 'Username is required' })}
                  type="text"
                  id="username"
                  className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                  placeholder="Enter your username"
                  autoComplete="username"
                />
                {errors.username && (
                  <p className="text-red-400 text-sm mt-1">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-vapour-700 mb-1">
                  Password
                </label>
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  id="password"
                  className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember-staff"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-vapour-600 focus:ring-vapour-500 border-dark-300 rounded bg-dark-200"
                />
                <label htmlFor="remember-staff" className="ml-2 block text-sm text-vapour-700">
                  Remember username
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-dark-200">
              <p className="text-center text-sm text-vapour-600">
                Customer?{' '}
                <Link to="/" className="text-vapour-800 hover:text-vapour-900 font-medium">
                  Join VapourVape Rewards
                </Link>
              </p>
            </div>

            {/* Staff Info */}
            <div className="mt-4 p-3 bg-vapour-100 border border-vapour-200 rounded-lg">
              <h3 className="text-sm font-semibold text-vapour-900 mb-2">🔐 Staff Access</h3>
              <div className="text-xs text-vapour-700 space-y-1">
                <div>• Scan customer QR codes to add stamps</div>
                <div>• Process reward redemptions</div>
                <div>• View customer activity and stats</div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  )
}